import { useState } from "react";
import {
    MessageBar,
    MessageBarType,
    ProgressIndicator,
    Stack,
    Text,
    DefaultButton,
    PrimaryButton,
    IStackTokens,
    Icon,
    TooltipHost,
    DirectionalHint
} from "@fluentui/react";
import styles from "./TokenBudgetIndicator.module.css";

export interface BudgetOption {
    action: "limit_notes" | "switch_mode" | "refine_filters" | "change_model";
    label: string;
    description: string;
    value: number | string | null;
    recommended?: boolean;
}

export interface TokenEstimate {
    totalCharacters: number;
    estimatedTokens: number;
    noteCount: number;
    contentMode: string;
    modelMaxTokens: number;
    safeLimit: number;
    modelName: string;
    exceedsLimit: boolean;
    utilizationPercent: number;
    // Budget-aware fields (may be missing on older backends)
    totalAvailable?: number;
    recommendedCount?: number;
    budgetStatus?: "within" | "warning" | "exceeded" | "error";
    actionRequired?: boolean;
    options?: BudgetOption[];
    targetUtilization?: number;
    error?: string;
    // Semantic limit fields
    semanticLimitExceeded?: boolean;
    semanticLimit?: number;
    requestedCount?: number; // Original requested count before semantic cap
}

interface Props {
    estimate: TokenEstimate | null;
    isLoading: boolean;
    onOptionSelect: (option: BudgetOption) => void;
    onOpenDeveloperSettings?: () => void;
    onOpenClinicalSettings?: () => void;
    compact?: boolean;
}

const stackTokens: IStackTokens = { childrenGap: 8 };

export const TokenBudgetIndicator = ({ estimate, isLoading, onOptionSelect, onOpenDeveloperSettings, onOpenClinicalSettings, compact = false }: Props) => {
    const [showOptions, setShowOptions] = useState(false);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <ProgressIndicator label="Calculating token budget..." />
            </div>
        );
    }

    if (!estimate) {
        return null;
    }

    if (estimate.error) {
        return (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                Error estimating tokens: {estimate.error}
            </MessageBar>
        );
    }

    // Derive budgetStatus if not provided (backwards compatibility)
    const budgetStatus = estimate.budgetStatus || (estimate.utilizationPercent <= 80 ? "within" : estimate.utilizationPercent <= 100 ? "warning" : "exceeded");
    const actionRequired = estimate.actionRequired ?? budgetStatus === "exceeded";
    const totalAvailable = estimate.totalAvailable ?? estimate.noteCount;
    const recommendedCount = estimate.recommendedCount ?? estimate.noteCount;
    const options = (estimate.options ?? []).filter(option => option.action !== "switch_mode");
    const semanticLimitExceeded = estimate.semanticLimitExceeded ?? false;
    const semanticLimit = estimate.semanticLimit ?? 50;
    const requestedCount = estimate.requestedCount ?? estimate.noteCount;

    const utilizationPercent = Math.min(estimate.utilizationPercent, 150); // Cap for display
    const barColor =
        budgetStatus === "within"
            ? "#107c10" // Green
            : budgetStatus === "warning"
              ? "#ffb900" // Yellow
              : "#d13438"; // Red

    const formatNumber = (n: number) => {
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${Math.round(n / 1000)}K`;
        return n.toLocaleString();
    };

    const contentModeLabel =
        {
            full: "Full Notes",
            summary: "AI Summaries",
            semantic: "Semantic Snippets"
        }[estimate.contentMode] || estimate.contentMode;

    if (compact) {
        // Compact view - bar, key numbers, and expandable options
        return (
            <div className={styles.compactWrapper}>
                <div
                    className={`${styles.compactContainer} ${(actionRequired || semanticLimitExceeded) && options.length > 0 ? styles.clickable : ""}`}
                    onClick={() => (actionRequired || semanticLimitExceeded) && options.length > 0 && setShowOptions(!showOptions)}
                    title={
                        (actionRequired || semanticLimitExceeded) && options.length > 0
                            ? "Click to see options"
                            : estimate.contentMode === "semantic"
                              ? `${requestedCount} of ${semanticLimit} notes (Snippets mode)`
                              : `${formatNumber(estimate.estimatedTokens)} tokens · ${estimate.modelName}`
                    }
                >
                    <div className={styles.compactBar}>
                        {estimate.contentMode === "semantic" ? (
                            <>
                                <div
                                    className={styles.compactBarFill}
                                    style={{
                                        width: `${Math.min((requestedCount / semanticLimit) * 100, 100)}%`,
                                        backgroundColor: semanticLimitExceeded ? "#d13438" : "#0078d4"
                                    }}
                                />
                                {semanticLimitExceeded && (
                                    <div
                                        className={styles.compactBarOverflow}
                                        style={{ width: `${Math.min(((requestedCount - semanticLimit) / semanticLimit) * 100, 50)}%` }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <div
                                    className={styles.compactBarFill}
                                    style={{
                                        width: `${Math.min(utilizationPercent, 100)}%`,
                                        backgroundColor: barColor
                                    }}
                                />
                                {utilizationPercent > 100 && (
                                    <div className={styles.compactBarOverflow} style={{ width: `${Math.min(utilizationPercent - 100, 50)}%` }} />
                                )}
                            </>
                        )}
                    </div>
                    <span className={styles.compactText}>
                        {estimate.contentMode === "semantic"
                            ? `${Math.min(requestedCount, semanticLimit)} of ${semanticLimit} notes`
                            : `${estimate.noteCount} notes · ${formatNumber(estimate.estimatedTokens)} tokens · ${Math.round(estimate.utilizationPercent)}%`}
                    </span>
                    {(actionRequired || semanticLimitExceeded) && <Icon iconName="Warning" className={styles.warningIcon} />}
                </div>

                {/* Expanded options panel */}
                {showOptions && (actionRequired || semanticLimitExceeded) && options.length > 0 && (
                    <div className={styles.optionsPanel}>
                        <Text variant="small" style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>
                            {semanticLimitExceeded
                                ? `⚠️ Snippets mode limited to ${semanticLimit} notes (${requestedCount} available). Choose an option:`
                                : "⚠️ Selection exceeds context limit. Choose an option:"}
                        </Text>
                        <Stack tokens={{ childrenGap: 6 }}>
                            {options.map((option, idx) => {
                                const ButtonComponent = option.recommended ? PrimaryButton : DefaultButton;
                                return (
                                    <ButtonComponent
                                        key={idx}
                                        text={option.recommended ? `${option.label} (Recommended)` : option.label}
                                        secondaryText={option.description}
                                        onClick={() => {
                                            if (option.action === "change_model" && onOpenDeveloperSettings) {
                                                onOpenDeveloperSettings();
                                            } else if (option.action === "refine_filters" && onOpenClinicalSettings) {
                                                onOpenClinicalSettings();
                                            } else {
                                                onOptionSelect?.(option);
                                            }
                                            setShowOptions(false);
                                        }}
                                        styles={{
                                            root: {
                                                width: "100%",
                                                height: "auto",
                                                padding: "8px 12px",
                                                textAlign: "left"
                                            },
                                            flexContainer: {
                                                flexDirection: "column",
                                                alignItems: "flex-start"
                                            },
                                            textContainer: { textAlign: "left" },
                                            secondaryText: {
                                                color: option.recommended ? "#fff" : "#605e5c",
                                                fontSize: 11
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                        <DefaultButton text="Dismiss" onClick={() => setShowOptions(false)} styles={{ root: { marginTop: 8 } }} />
                    </div>
                )}
            </div>
        );
    }

    // Full view
    return (
        <div className={styles.container}>
            <Stack tokens={stackTokens}>
                {/* Header with utilization bar */}
                <div className={styles.header}>
                    <Text variant="smallPlus" className={styles.title}>
                        <Icon iconName="Processing" /> Token Budget
                    </Text>
                    <Text variant="small" className={styles.modelName}>
                        {estimate.modelName}
                    </Text>
                </div>

                {/* Progress bar */}
                <div className={styles.barContainer}>
                    <div className={styles.bar}>
                        <div
                            className={styles.barFill}
                            style={{
                                width: `${Math.min(utilizationPercent, 100)}%`,
                                backgroundColor: barColor
                            }}
                        />
                        {utilizationPercent > 100 && <div className={styles.barOverflow} style={{ width: `${Math.min(utilizationPercent - 100, 50)}%` }} />}
                    </div>
                    <Text variant="small" className={styles.percentText}>
                        {Math.round(estimate.utilizationPercent)}%
                    </Text>
                </div>

                {/* Stats row */}
                <div className={styles.statsRow}>
                    <div className={styles.stat}>
                        <Text variant="small" className={styles.statLabel}>
                            Notes
                        </Text>
                        <Text variant="mediumPlus" className={styles.statValue}>
                            {semanticLimitExceeded ? (
                                <>
                                    <span style={{ color: "#d13438" }}>{semanticLimit}</span>
                                    <span className={styles.statSuffix}> / {requestedCount}</span>
                                </>
                            ) : (
                                <>
                                    {estimate.noteCount}
                                    <span className={styles.statSuffix}> / {totalAvailable}</span>
                                </>
                            )}
                        </Text>
                    </div>
                    <div className={styles.stat}>
                        <Text variant="small" className={styles.statLabel}>
                            Tokens
                        </Text>
                        <Text variant="mediumPlus" className={styles.statValue}>
                            {formatNumber(estimate.estimatedTokens)}
                        </Text>
                    </div>
                    <div className={styles.stat}>
                        <Text variant="small" className={styles.statLabel}>
                            Content
                        </Text>
                        <Text variant="mediumPlus" className={styles.statValue}>
                            {contentModeLabel}
                        </Text>
                    </div>
                </div>

                {/* Semantic limit warning */}
                {semanticLimitExceeded && (
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={true}
                        actions={options.length > 0 ? <DefaultButton onClick={() => setShowOptions(!showOptions)}>{showOptions ? "Hide Options" : "Show Options"}</DefaultButton> : undefined}
                    >
                        <strong>Snippets mode limited to {semanticLimit} notes.</strong>
                        <br />
                        {requestedCount} notes available. Semantic search will use the top {semanticLimit} by relevance.
                    </MessageBar>
                )}

                {/* Warning/Error message (only show if not semantic-limited) */}
                {!semanticLimitExceeded && budgetStatus === "warning" && (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
                        Approaching context limit. Consider refining your selection.
                    </MessageBar>
                )}

                {!semanticLimitExceeded && budgetStatus === "exceeded" && (
                    <MessageBar
                        messageBarType={MessageBarType.severeWarning}
                        isMultiline={true}
                        actions={options.length > 0 ? <DefaultButton onClick={() => setShowOptions(!showOptions)}>{showOptions ? "Hide Options" : "Show Options"}</DefaultButton> : undefined}
                    >
                        <strong>Selection exceeds context window.</strong>
                        <br />
                        {recommendedCount} notes fit within budget (vs {estimate.noteCount} selected).
                    </MessageBar>
                )}

                {/* Options panel */}
                {showOptions && options.length > 0 && (
                    <div className={styles.optionsPanel}>
                        <Text variant="smallPlus" className={styles.optionsTitle}>
                            Choose an option:
                        </Text>
                        <Stack tokens={{ childrenGap: 6 }}>
                            {options.map((option, idx) => (
                                <div key={idx} className={styles.optionItem}>
                                    <PrimaryButton
                                        className={styles.optionButton}
                                        onClick={() => {
                                            onOptionSelect(option);
                                            setShowOptions(false);
                                        }}
                                    >
                                        {option.label}
                                    </PrimaryButton>
                                    <Text variant="small" className={styles.optionDescription}>
                                        {option.description}
                                    </Text>
                                </div>
                            ))}
                        </Stack>
                    </div>
                )}
            </Stack>
        </div>
    );
};
